<template>
  <form-textarea
    :name="name"
    :label="label"
    :resize="resize"
    :classes="classes"
    :placeholder="placeholder"
    :input-value="inputValue"
    :required="required"
    :disabled="isDisabled"
    :is-update-in-process="planetStore.isUpdateInProcess"
    :ignore-update-in-process="ignoreUpdateInProcess"
    :cols="cols"
    :rows="rows"
    :is-invalid="isInvalid"
    :error-message-text="errorMessageText"
    :tooltip="tooltip"
    :data-qa="dataQa"
    ref="formTextareaRef"
    v-bind="$attrs"
    @update:input-value="(value) => emit('update:inputValue', value || '')"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  >
    <template #postLabel v-if="$slots.postLabel">
      <slot name="postLabel"></slot>
    </template>
  </form-textarea>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import FormTextarea from 'ui-component-library/rbz/components/FormTextarea.vue';
import { usePlanetStore } from '@/store/PlanetStore';

interface RbzFormTextareaProps {
  name: string;
  label?: string;
  resize?: string;
  classes?: string;
  placeholder?: string;
  inputValue: string;
  required?: boolean;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  cols?: string | number;
  rows?: string | number;
  isInvalid?: boolean;
  errorMessageText?: string;
  tooltip?: string;
  dataQa?: string;
}

const planetStore = usePlanetStore();
const props = withDefaults(defineProps<RbzFormTextareaProps>(), {
  label: '',
  classes: '',
  placeholder: '',
  inputValue: '',
  required: false,
  disabled: false,
  cols: null,
  rows: null,
  errorMessageText: '',
  resize: 'vertical',
  tooltip: '',
  dataQa: '',
});

interface RbzFormTextareaEmits {
  (e: 'update:inputValue', value: string | number): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<RbzFormTextareaEmits>();

const isDisabled = computed(() => {
  return props.disabled || (planetStore.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const formTextareaRef = ref<FormTextarea | null>(null);

defineExpose({
  setIsDirty: (value: boolean) => formTextareaRef.value?.setIsDirty(value),
});
</script>
